<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import { useIntervalFn } from '@vueuse/core';
  import InvoicingAfipService from '@/api/services/invoicing/invoicing-afip.service';
  import { useSocketStore } from '@/composition/use-socket';
  import { IInvoicingData } from '@/interfaces/IInvoicingData';
  import {
    IInvoicingSetupJob,
    IInvoicingSetupWsPayload,
  } from '@/interfaces/IInvoicingSetupJob';

  const props = defineProps<{
    jobPublicId: string;
    restaurantId: number;
  }>();

  const emit = defineEmits<{
    (e: 'succeeded', invoicingData: IInvoicingData | null): void;
    (e: 'retry'): void;
  }>();

  const socketStore = useSocketStore();

  const STEPS = [
    { key: 'cert', label: 'Generando certificado digital' },
    { key: 'ws-auth', label: 'Autorizando servicios' },
    { key: 'finalize', label: 'Finalizando configuración' },
  ];

  const job = ref<IInvoicingSetupJob | null>(null);
  const settled = ref(false);

  const failed = computed(() => job.value?.status === 'failed');

  const errorMessage = computed(
    () =>
      job.value?.errorMessage ||
      'No se pudo completar la configuración. Vuelva a intentarlo.'
  );

  const currentStepIndex = computed(() => {
    const idx = STEPS.findIndex((s) => s.key === job.value?.step);
    return idx === -1 ? 0 : idx;
  });

  const { pause } = useIntervalFn(() => {
    refresh().catch(() => undefined);
  }, 5000);

  function settle() {
    settled.value = true;
    pause();
  }

  function applyJob(fresh: IInvoicingSetupJob) {
    if (settled.value) return;
    job.value = fresh;

    if (fresh.status === 'succeeded') {
      settle();
      emit('succeeded', fresh.invoicingData ?? null);
    } else if (fresh.status === 'failed') {
      // Stay mounted showing the error; the retry button hands control back.
      settle();
    }
  }

  async function refresh() {
    if (settled.value) return;
    try {
      const fresh = await InvoicingAfipService.getSetupJob(props.jobPublicId);
      applyJob(fresh);
    } catch (err) {
      // Transient (network blip): the next poll retries.
      console.error('[InvoicingSetupProgress] Refresh failed', err);
    }
  }

  const wsEvent = `r:${props.restaurantId}:invoicingSetup`;

  function onWsUpdate(payload: IInvoicingSetupWsPayload) {
    if (payload?.publicId !== props.jobPublicId) return;
    // WS payloads carry no invoicingData: fetch the full job state.
    refresh().catch(() => undefined);
  }

  onMounted(() => {
    socketStore.socket?.on(wsEvent, onWsUpdate);
    refresh().catch(() => undefined);
  });

  onUnmounted(() => {
    pause();
    socketStore.socket?.off(wsEvent, onWsUpdate);
  });
</script>

<template>
  <div class="invoicing-setup-progress">
    <template v-if="!failed">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        class="progress-spinner"
      ></v-progress-circular>
      <h3 class="progress-title">Creando autorización</h3>
      <div class="progress-steps">
        <div
          v-for="(s, index) in STEPS"
          :key="s.key"
          class="progress-step"
          :class="{
            'progress-step--done': index < currentStepIndex,
            'progress-step--active': index === currentStepIndex,
          }"
        >
          <v-icon
            small
            :color="
              index < currentStepIndex
                ? 'success'
                : index === currentStepIndex
                ? 'primary'
                : 'grey'
            "
          >
            {{
              index < currentStepIndex
                ? 'mdi-check-circle'
                : index === currentStepIndex
                ? 'mdi-progress-clock'
                : 'mdi-circle-outline'
            }}
          </v-icon>
          <span class="progress-step-label">{{ s.label }}</span>
        </div>
      </div>
      <p class="progress-hint">
        Este proceso puede tardar varios minutos. Puede salir de esta pantalla,
        la configuración continuará.
      </p>
    </template>
    <template v-else>
      <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
      <p class="progress-error">{{ errorMessage }}</p>
      <v-btn color="primary" @click="emit('retry')">Reintentar</v-btn>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .invoicing-setup-progress {
    @apply tw-flex tw-flex-col tw-items-center tw-text-center tw-py-8 tw-space-y-4;

    .progress-title {
      @apply tw-text-lg tw-font-semibold;
    }

    .progress-steps {
      @apply tw-flex tw-flex-col tw-items-start tw-space-y-2;

      .progress-step {
        @apply tw-flex tw-items-center tw-space-x-2 tw-text-gray-400;

        &--done {
          @apply tw-text-gray-700;
        }

        &--active {
          @apply tw-text-gray-900 tw-font-medium;
        }

        .progress-step-label {
          @apply tw-text-sm;
        }
      }
    }

    .progress-hint {
      @apply tw-text-sm tw-text-gray-500 tw-max-w-md;
    }

    .progress-error {
      @apply tw-text-base tw-text-red-600 tw-max-w-md;
    }
  }
</style>
